
import { StoreModel } from '@/models/store';
import { ReportModel } from '@/models/report';
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { AnalyticsTypeModel } from '@/models/analytics_type';
import { DimensionModel } from '@/models/dimension';
import { MeasureModel } from '@/models/measure';
import { FilterColumnList } from '@/collections/filter_columns';
import { ParamList } from '@/collections/params';
import { MetricList } from '@/collections/metrics';
import { ProductSegmentModel } from '@/models/product_segment';
import { compositionConfig, CompositionDefinition } from '@/config/compositionConfig';
import CompositionCellRenderer from './CompositionCellRenderer.vue';
import { Column } from '@/models/report';

@Component({
  components: {
    CompositionCellRenderer,
  },
})
export default class DTotal extends Vue {
  public page: number = 1;
  public perPage: number = 0;
  @Prop({ default: () => [] })
  public headers!: any[];
  public rows: any = [];
  @Prop({ default: () => [] })
  public initSelectedReportColumns!: any[];
  public selectedReportColumns: any = [];
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  public messageColor: string = '';
  public message: string = 'Please selelect a dashboard to save the card';
  public loadingSlides: string[] = [
    'Connecting to analytic services',
    'Fetching data',
    'Building report',
    'Downloading data',
    'Generating card data',
  ];
  @Prop({ default: () => new MeasureModel() })
  public measure!: MeasureModel;
  @Prop({ default: () => new DimensionModel() })
  public dimension!: DimensionModel;
  @Prop({ default: () => new AnalyticsTypeModel() })
  public analyticsType!: AnalyticsTypeModel;
  @Prop({ default: () => [] })
  public filterTimerange!: string[];
  @Prop({ default: () => '30d' })
  public defaultRange!: string;
  public report: ReportModel = new ReportModel();
  public isLoading: boolean = false;
  public errorData: boolean = false;
  @Prop({ default: () => new FilterColumnList() })
  public filterColumns!: FilterColumnList;
  @Prop({ default: () => new ParamList() })
  public reportParams!: ParamList;
  @Prop({ default: false })
  public isResetFilterColumns!: boolean;
  @Prop({ default: '' })
  public pivotOption!: string;
  @Prop({default: () => new MetricList()})
  public calculatedMetrics!: MetricList;
  @Prop({default: () => new ProductSegmentModel()})
  public productSegment!: ProductSegmentModel;
  @Prop({ default: 3 })
  public maxPinColumn!: number;
  @Prop({ default: () => ({}) })
  public dTotalOffsets!: { [index: number]: string };

  public get selectedColumnCodesSet(): Set<string> {
    if (!this.initSelectedReportColumns || this.initSelectedReportColumns.length === 0) {
       return new Set<string>();
    }
    return new Set(this.initSelectedReportColumns.map((c: any) => c.code));
  }

  public async created() {
    await this.getData();
  }

  public async getData() {
    try {
      this.isLoading = true;
      this.report.measure = this.measure;
      this.report.dimension = this.dimension;
      this.report.analyticsType = this.analyticsType;
      this.report.filterTimerange = this.filterTimerange;
      this.report.filterColumns = this.filterColumns;
      this.report.reportParams = this.reportParams;
      this.report.reportColumns = this.initSelectedReportColumns;
      this.report.typeReport = 'BIG_NUMBER';
      this.report.isDetail = false;
      this.report.isMetric = true;
      this.report.page = 1;
      this.report.perPage = 0;
      this.report.calculatedMetrics = this.calculatedMetrics;
      this.report.productSegment = this.productSegment;
      if (this.pivotOption && this.pivotOption !== 'undefined') {
        this.report.pivotOption = this.pivotOption;
      }
      await this.report.getReport();

      this.reloadTable();
      this.isLoading = false;
      this.$emit('dtotal-rendered');
    } catch (error: any) {
      if (error.code !== 'ERR_CANCELED') {
        // this.$emit('error', error);
        this.errorData = true;
        this.isLoading = false;
      }
    }
  }

  public getTotalCellClass(header: any, index: number) {
    let cellClass = 'data-table-cell';

    if (header.value === 'actions') {
      cellClass += ' data-table-cell-actions d-print-none';
    }

    if (header.isPin) {
      cellClass += ' pinned-column';
      cellClass += ` pin-column${this.getPinnedColumnIndex(header)}`;
      if (this.isLastPinnedColumn(header)) {
        cellClass += ' last-pinned-column';
      }
    }

    return cellClass;
  }

  public getPinnedColumnIndex(header: any): number {
    return this.headers.filter((h) => h.isPin).indexOf(header) + 1;
  }

  public isLastPinnedColumn(header: any): boolean {
    const pinnedHeaders = this.headers.filter((h) => h.isPin);
    return pinnedHeaders[pinnedHeaders.length - 1]?.value === header.value;
  }

  public removeUnselectedColumn() {
    const report = new ReportModel();
    for (const column of this.report.columns) {
      if (this.isSelectedColumn(column.code)) {
        report.columns.push(column);
      }
    }
    this.report.columns = report.columns;
  }

  public generateSelectedReprotColumns() {
    if (!this.initSelectedReportColumns || this.initSelectedReportColumns.length === 0) {
      if (this.report.columns.length > 0) {
        this.selectedReportColumns = this.report.columns;
      }
    } else {
      this.selectedReportColumns = this.initSelectedReportColumns;
    }
  }

  public reloadTable() {
    this.generateSelectedReprotColumns();
    this.removeUnselectedColumn();
    this.rows = [];
    for (const index in this.report.rows) {
      if (this.report.rows[index]) {
        const res: any = {};
        for (const column of this.report.columns) {
          if (!this.isSelectedColumn(column.code)) {
            continue;
          }
          if (column.values[index]) {
            res[column.code] = column.values[index];
          } else {
            if (column.dataType === 'number'
              || column.dataType === 'decimal'
              || column.dataType === 'currency'
              || column.dataType === 'percent') {
              res[column.code] = '0';
            } else {
              res[column.code] = '-';
            }
          }
        }
        this.rows.push(res);
      }
    }
  }

  public isSelectedColumn(code: string) {
    if (!this.selectedReportColumns || this.selectedReportColumns.length === 0) {
      return true;
    }
    for (const c of this.selectedReportColumns) {
      if (c.code === code) {
        return true;
      }
    }
    return false;
  }

  public getTableCellClass(header: any, index: number) {
    let cellClass = '';
    if (header.value === 'actions') {
      cellClass += 'data-table-cell-actions d-print-none';
    } else {
      cellClass += 'data-table-cell';
    }

    if (header.isPin) {
      cellClass += ' pinned-column';
      cellClass += ` pin-column${this.getPinnedColumnIndex(header)}`;
      if (this.isLastPinnedColumn(header)) {
        cellClass += ' last-pinned-column';
      }
    }

    return cellClass;
  }

  public getDTotalCellStyle(header: any, index: number): object {
    const styleObject: { left?: string; borderRight?: string } = {};

    if (header.isPin && this.dTotalOffsets && this.dTotalOffsets[index] !== undefined) {
      styleObject.left = this.dTotalOffsets[index];
    }

    const hasContent = this.rows.length > 0 && (this.rows[0][header.value] || header.dataType === 'composition');
    const isNotFirstActionCell = index > 0;
    const isNotLastCell = index < this.headers.length - 1;

    if (hasContent && isNotFirstActionCell && isNotLastCell) {
      styleObject.borderRight = 'thin solid rgba(0, 0, 0, 0.12)';
    }

    return styleObject;
  }

  public getCompositionConfigForKey(key: string): CompositionDefinition | undefined {
    return compositionConfig[key];
  }
}
